import I18n from 'I18n';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import SkeletonText from 'automation-ui-core/components/SkeletonText';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import * as PropertyValueTypes from 'automation-ui-flow-core/fields/PropertyValueTypes';
import * as SetPropertyActionOperations from 'automation-ui-action-features/actions/set-property/SetPropertyActionOperations';
import inferPropertyActionOperationType from 'automation-ui-action-features/actions/set-property/utils/inferPropertyActionOperationType';
import { getObjectTypeNameMessageOptions } from 'automation-ui-action-features/actions/set-property/utils/getObjectTypeNameMessageOptions';
import DataSourcePropertyName from 'automation-ui-formula-editor/data-sources/DataSourcePropertyName';
import DataSourceName from 'automation-ui-formula-editor/data-sources/DataSourceName';
import { useAssociationDefinitions } from 'automation-ui-associations/hooks/associationDefinitionsHooks';
import { useTargetProperty } from 'automation-ui-action-features/actions/set-property/wireTargetProperty';
import CRMPropertyFieldTypes from 'automation-ui-flow-core/fields/CRMPropertyFieldTypes';
import { useExternalOptions } from 'automation-ui-action-features/external-options/externalOptionsDecorator';
import PersonalizedTextView from 'automation-ui-action-features/personalized-text/PersonalizedTextView';
import { getIsAssociation } from 'automation-ui-action-features/actions/set-property/utils/associationUtils';
import MissingLabel from 'automation-ui-core/components/MissingLabel';
import { jsx as _jsx } from "react/jsx-runtime";
export default function SetPropertyActionSummary({
  actionMetadata,
  flowObjectTypeId,
  getActionLabelById
}) {
  const associationDefinitionsQuery = useAssociationDefinitions([flowObjectTypeId]);
  const {
    // targetObjectTypeId will be undefined until association definitions are loaded
    targetObjectTypeId,
    targetProperty,
    targetPropertyValueLabel
  } = useTargetProperty({
    actionMetadata,
    associationDefinitionsQuery,
    flowObjectTypeId,
    omitQuotes: true
  });
  const operation = inferPropertyActionOperationType(actionMetadata);
  const valueType = actionMetadata.getValueType();
  const targetPropertyName = actionMetadata.getTargetPropertyName();
  const sourcePropertyName = actionMetadata.getValuePropertyName();
  const isNotFieldData = valueType !== PropertyValueTypes.FIELD_DATA;
  const isPercentageValue = !!targetProperty && targetProperty.fieldType === CRMPropertyFieldTypes.NUMBER && targetProperty.numberDisplayHint === 'percentage';

  // External Options value takes precedence.
  const {
    references: externalOptionReferences
  } = useExternalOptions({
    property: targetProperty,
    propertyTypeId: targetObjectTypeId,
    externalOptionValue: actionMetadata.getValueValue()
  });
  const valueOverride = externalOptionReferences ? I18n.formatList(externalOptionReferences.map(reference => reference.label)) : targetPropertyValueLabel || '';
  const isAssociation = getIsAssociation({
    metadata: actionMetadata
  });
  const messageOptions = Object.assign({
    inputActionLabel: /*#__PURE__*/_jsx("strong", {
      children: getActionLabelById({
        actionId: actionMetadata.getValueIdentifierActionId()
      })
    }),
    inputDataPropertyName: /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(DataSourcePropertyName, {
        token: actionMetadata.getValuePropertyToken()
      })
    }),
    inputDataSourceLabel: /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(DataSourceName, {
        token: actionMetadata.getValuePropertyToken()
      })
    }),
    amount: /*#__PURE__*/_jsx("strong", {
      children: Math.abs(actionMetadata.getValueAmount())
    })
  }, getObjectTypeNameMessageOptions({
    flowObjectTypeId,
    targetObjectTypeId
  }), {
    sourcePropertyName: sourcePropertyName && isNotFieldData ? /*#__PURE__*/_jsx(PropertyName, {
      propertyName: sourcePropertyName,
      objectTypeId: flowObjectTypeId
    }) : null,
    targetPropertyName: targetObjectTypeId && targetPropertyName ? /*#__PURE__*/_jsx(PropertyName, {
      propertyName: targetPropertyName,
      objectTypeId: targetObjectTypeId
    }) : /*#__PURE__*/_jsx(SkeletonText, {}),
    value: !valueOverride ? /*#__PURE__*/_jsx(MissingLabel, {
      type: "value"
    }) : /*#__PURE__*/_jsx("strong", {
      children: isPercentageValue ? /*#__PURE__*/_jsx(FormattedPercentage, {
        precision: 2,
        value: Number(valueOverride) * 100
      }) : /*#__PURE__*/_jsx(PersonalizedTextView, {
        propertyTypeId: flowObjectTypeId,
        text: valueOverride
      })
    })
  });
  let message = '';
  if (!targetPropertyName) {
    message = 'automation-ui-action-features.action-features.set-property.SET.summary.missing-target-property';
  } else if (operation === SetPropertyActionOperations.INCREASE) {
    const isIncreasing = actionMetadata.getValueAmount() >= 0;
    if (isIncreasing) {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.INCREASE.summary.associated.increase' : 'automation-ui-action-features.action-features.set-property.INCREASE.summary.enrolled.increase';
    } else {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.INCREASE.summary.associated.decrease' : 'automation-ui-action-features.action-features.set-property.INCREASE.summary.enrolled.decrease';
    }
  } else if (actionMetadata.getIsValueEmpty()) {
    message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.clear-property.associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.clear-property.enrolled';
  } else {
    // operation === SetPropertyActionOperations.SET
    if (valueType === PropertyValueTypes.STATIC_APPEND_VALUE) {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.static-append-value.associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.static-append-value.enrolled';
    } else if (valueType === PropertyValueTypes.TIMESTAMP) {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.date-of-step.associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.date-of-step.enrolled';
    } else if (valueType === PropertyValueTypes.FETCHED_OBJECT_PROPERTY || valueType === PropertyValueTypes.ENROLLMENT_EVENT_PROPERTY) {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.fetched-data.data-token.associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.fetched-data.data-token.enrolled';
    } else if (valueType === PropertyValueTypes.OBJECT_PROPERTY) {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.object-property-value-associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.object-property-value';
    } else if (valueType === PropertyValueTypes.APPEND_OBJECT_PROPERTY) {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.append-property.associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.append-property.enrolled';
    } else if (valueType === PropertyValueTypes.FIELD_DATA) {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.field-data-value.associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.field-data-value.enrolled';
    } else {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.summary.associated' : 'automation-ui-action-features.action-features.set-property.SET.summary.enrolled';
    }
  }
  return /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: message,
    options: messageOptions
  });
}